import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const HAS_IMAGE_FILTER_PARAM = 'filter.p.tag';
const HAS_IMAGE_FILTER_VALUE = 'has-image';
const ADOPTED_TITLE_FILTER_VALUE = 'adoptedtitle';
const ISBN_PARAM = 'filter.p.m.course_materials.isbn_ean';

function ensureHasImageFilter(searchParams) {
  const hasHasImage = searchParams.getAll(HAS_IMAGE_FILTER_PARAM).includes(HAS_IMAGE_FILTER_VALUE);

  if (!hasHasImage) {
    searchParams.append(HAS_IMAGE_FILTER_PARAM, HAS_IMAGE_FILTER_VALUE);
  }
}

function ensureAdoptedTitleFilter(searchParams) {
  const hasAdoptedTitle = searchParams.getAll(HAS_IMAGE_FILTER_PARAM).includes(ADOPTED_TITLE_FILTER_VALUE);

  if (!hasAdoptedTitle) {
    searchParams.append(HAS_IMAGE_FILTER_PARAM, ADOPTED_TITLE_FILTER_VALUE);
  }
}

/**
 * A custom element that allows the user to clean a search input.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchPageInput - The search input element.
 * @extends {Component<Refs>}
 */
class SearchPageInputComponent extends Component {
  requiredRefs = ['searchPageInput'];

  connectedCallback() {
    super.connectedCallback();

    this.#syncInputWithISBNSearchParam();

    // Add form submit handler for ISBN searches
    const form = this.parentElement;
    if (form && form.tagName.toLowerCase() === "form") {
      form.addEventListener('submit', this.#handleFormSubmit);
    }
  }

  /**
   * Handle form submission - redirect to ISBN search if applicable
   * @param {Event} event - The form submit event
   */
  #handleFormSubmit = (event) => {
    const searchTerm = this.refs.searchPageInput.value.trim();

    if (this.#isISBN(searchTerm)) {
      event.preventDefault();
      const searchUrl = new URL('/search', location.origin);
      searchUrl.searchParams.set(
        'filter.p.m.course_materials.isbn_ean',
        searchTerm
      );
      searchUrl.searchParams.set('q', '*');
      window.location.href = searchUrl.toString();
    }
    // Otherwise, let the form submit naturally to routes.search_url
  };

  /**
   * Handles the click event on the clear button and submits an empty search.
   * This clears the search input and resubmits the form if the page is not
   * already in an empty state.
   */
  handleClearClick() {
    this.#submitEmptySearch();
  }

  /**
   * Handles the keydown event on the search input and resets the search when
   * empty and Escape is pressed.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown = debounce((event) => {
    const value = this.refs.searchPageInput.value.trim();

    if (event.key === 'Escape' && value === '') {
      this.#submitEmptySearch();
    }
  }, 100);

  #submitEmptySearch() {
    const searchInput = this.refs.searchPageInput;

    searchInput.focus();
    searchInput.value = '';

    if (this.#isEmptyState()) return;

    searchInput.form?.submit();
  }

  #syncInputWithISBNSearchParam() {
    const url = new URL(window.location.href);
    const queryParam = (url.searchParams.get('q') ?? '').trim();
    const isbnParam = (url.searchParams.get(ISBN_PARAM) ?? '').trim();

    if (queryParam === '*' && this.#isISBN(isbnParam)) {
      this.refs.searchPageInput.value = isbnParam;
    }
  }

  /**
   * Check if search term is an ISBN (10 or 13 digits)
   * @param {string} searchTerm - The search term to check
   * @returns {boolean} True if ISBN-10 or ISBN-13
   */
  #isISBN(searchTerm) {
    const trimmed = searchTerm.trim();
    return /^\d{10}$/.test(trimmed) || /^\d{13}$/.test(trimmed);
  }

  #isEmptyState = () => {
    const url = new URL(window.location.href);
    const queryParam = url.searchParams.get('q') ?? '';

    return queryParam.trim() === '';
  };
}

if (!customElements.get('search-page-input-component')) {
  customElements.define('search-page-input-component', SearchPageInputComponent);
}
